namespace gotoAndPlay {
    interface IFilterParams {
        [group: string]: string[];
    }

    export class ProductsFilter {
        element: JQuery;
        hideUrl: boolean;
        ajax;

        orderHash: string;
        categoryUrl: string;
        materialGroup: string;
        filterChangeDebounce: number | null = null;

        constructor(target: JQuery) {
            this.element = target;
            this.hideUrl = $('#hide__url-changes').data('hide') == true;
            this.orderHash = this.element.data('product-order-hash');
            this.categoryUrl = this.element.attr('data-category-url') || '';
            this.materialGroup = this.element.attr('data-material-group') || '';
            this.init();
        }

        static parseParams(search: string): IFilterParams {
            const params: IFilterParams = {};
            const query = search.replace(/^\?/, '');
            if (!query) {
                return params;
            }

            query.split('&').forEach((pair) => {
                const parts = pair.split('=');
                const key = decodeURIComponent(parts[0]);
                if (!key) {
                    return;
                }

                const value = parts.length > 1 ? decodeURIComponent(parts.slice(1).join('=')) : '';
                params[key] = value ? value.split(',').filter((item) => item !== '') : [];
            });

            return params;
        }

        static buildQuery(params: IFilterParams): string {
            const pairs: string[] = [];
            Object.keys(params).forEach((key) => {
                if (params[key].length > 0) {
                    pairs.push(encodeURIComponent(key) + '=' + params[key].map(encodeURIComponent).join(','));
                }
            });

            return pairs.length ? '?' + pairs.join('&') : '';
        }

        /**
         * Replaces the given filter groups in the URL's query string with the
         * given params, keeping every other parameter (e.g. lang) and the hash.
         */
        static mergeUrl(url: string, params: IFilterParams, groups: string[], hash: string): string {
            const hashIndex = url.indexOf('#');
            const withoutHash = hashIndex >= 0 ? url.substring(0, hashIndex) : url;
            const queryIndex = withoutHash.indexOf('?');
            const path = queryIndex >= 0 ? withoutHash.substring(0, queryIndex) : withoutHash;
            const existing = ProductsFilter.parseParams(queryIndex >= 0 ? withoutHash.substring(queryIndex) : '');

            groups.forEach((group) => {
                delete existing[group];
            });
            Object.keys(params).forEach((group) => {
                existing[group] = params[group];
            });

            return path + ProductsFilter.buildQuery(existing) + hash;
        }

        init(): void {
            this.getFilterInputs().change((event) => {
                const input = $(event.currentTarget);
                if (this.materialGroup && !this.hideUrl && input.attr('data-group') === this.materialGroup) {
                    this.navigateToMaterialSelection();

                    return;
                }

                if (this.filterChangeDebounce) {
                    clearTimeout(this.filterChangeDebounce);
                }

                this.filterChangeDebounce = window.setTimeout(this.handleDropdownChange, 500);
            });

            this.applyStateFromUrl();
        }

        getFilterInputs(): JQuery {
            return this.element.find('.multiselect-dropdown__input--check');
        }

        getFilterGroups(): string[] {
            const groups: string[] = [];
            this.getFilterInputs().each((index, element) => {
                const group = $(element).attr('data-group');
                if (group && groups.indexOf(group) < 0) {
                    groups.push(group);
                }
            });

            return groups;
        }

        findInput(group: string, slug: string): JQuery {
            return this.getFilterInputs().filter((index, element) => {
                return $(element).attr('data-group') === group && $(element).attr('data-slug') === slug;
            });
        }

        applyStateFromUrl(): void {
            const params = ProductsFilter.parseParams(window.location.search);
            let needUpdate = false;
            Object.keys(params).forEach((group) => {
                params[group].forEach((slug) => {
                    const input = this.findInput(group, slug);
                    if (input.length > 0 && !input.prop('checked')) {
                        input.prop('checked', true);
                        needUpdate = true;
                    }
                });
            });

            if (needUpdate) {
                this.handleDropdownChange();
            }
        }

        collectParams(): IFilterParams {
            const params: IFilterParams = {};
            this.getFilterInputs().each((index, element) => {
                if (!(element as HTMLInputElement).checked) {
                    return;
                }

                const group = $(element).attr('data-group');
                const slug = $(element).attr('data-slug');
                if (!group || !slug) {
                    return;
                }

                if (!params[group]) {
                    params[group] = [];
                }

                params[group].push(slug);
            });

            return params;
        }

        handleDropdownChange = (): void => {
            this.element.find('.product-list__list').addClass('is-loading');
            this.loadPosts(this.findActive());
            this.updateUrl();
        }

        updateUrl(): void {
            if (this.hideUrl) {
                return;
            }

            const params = this.collectParams();
            const materials = params[this.materialGroup] || [];
            if (this.materialGroup && materials.length === 1) {
                // A single material is already expressed by the page path.
                delete params[this.materialGroup];
            }

            const url = ProductsFilter.mergeUrl(
                window.location.href,
                params,
                this.getFilterGroups(),
                window.location.hash,
            );
            history.replaceState({}, document.title, url);
        }

        navigateToMaterialSelection(): void {
            const params = this.collectParams();
            const materials = params[this.materialGroup] || [];
            let base = this.categoryUrl || window.location.href;

            if (materials.length === 1) {
                const url = this.findInput(this.materialGroup, materials[0]).attr('data-url');
                if (url) {
                    base = url;
                    delete params[this.materialGroup];
                }
            }

            this.element.find('.product-list__list').addClass('is-loading');
            window.location.href = ProductsFilter.mergeUrl(base, params, this.getFilterGroups(), window.location.hash);
        }

        findActive(): string[][] {
            const activeFilters: string[][] = [];
            this.element.find('.filter__item').each((index, element) => {
                const selection: string[] = [];
                $(element).find('input').each((inputIndex, input) => {
                    if ((input as HTMLInputElement).checked) {
                        selection.push((input as HTMLInputElement).value);
                    }
                });
                activeFilters.push(selection);
            });

            return activeFilters;
        }

        loadPosts(ids: string[][]): void {
            const priced = $('#calculator').length > 0;
            this.ajax = $.post(ajaxPath, {
                filters: ids,
                action: 'filter-posts',
                cat: this.element.find('.js-current-cat-id').val(),
                mod: this.element.find('.js-current-mod').val(),
                id: $('#current-post-id').val(),
                priced: priced,
            });
            this.ajax.then((result) => {
                if (result.hash !== this.orderHash) {
                    this.orderHash = result.hash;
                    this.element.find('.js-products').html(result.data);
                }
            });
            this.ajax.always(() => {
                this.element.find('.product-list__list').removeClass('is-loading');
            });
        }
    }

    $(document).ready(function() {
        const productListContainers = $('.js-product-list__container');

        if (productListContainers.length > 0) {
            productListContainers.each((index, element) => {
                const productList = new ProductsFilter($(element));
            });
        }

        $('.multiselect-dropdown--check').each((index, element) => {
            const checked = $(element).find('.multiselect-dropdown__input--check:checked').length;

            $(element).find('.multiselect-dropdown__select span').remove();

            if (checked) {
                $(element).find('.multiselect-dropdown__select').append('<span>' + checked + '</span>');
            }
        });
    });
}
